import BarChartIcon from '@mui/icons-material/BarChart';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TagIcon from '@mui/icons-material/Tag';

export const CHART_TYPES = {
  BAR: 'bar',
  GROUPED_BAR: 'grouped-bar',
  STACKED_BAR: 'stacked-bar',
  LINE: 'line',
  BURNUP: 'burnup',
  BURNDOWN: 'burndown',
  PIE: 'pie',
  NUMBER: 'number',
};

export const CHART_TYPE_DEFINITIONS = {
  [CHART_TYPES.BAR]: {
    key: CHART_TYPES.BAR,
    label: 'Bar',
    icon: (props) => <BarChartIcon {...props} />,
  },
  [CHART_TYPES.GROUPED_BAR]: {
    key: CHART_TYPES.GROUPED_BAR,
    label: 'Grouped bar',
    icon: (props) => <BarChartIcon {...props} />,
  },
  [CHART_TYPES.STACKED_BAR]: {
    key: CHART_TYPES.STACKED_BAR,
    label: 'Stacked Bar',
    icon: (props) => <StackedBarChartIcon {...props} />,
  },
  [CHART_TYPES.LINE]: {
    key: CHART_TYPES.LINE,
    label: 'Line',
    icon: (props) => <ShowChartIcon {...props} />,
  },
  [CHART_TYPES.BURNUP]: {
    key: CHART_TYPES.BURNUP,
    label: 'Burnup',
    icon: (props) => <TrendingUpIcon {...props} />,
  },
  [CHART_TYPES.BURNDOWN]: {
    key: CHART_TYPES.BURNDOWN,
    label: 'Burndown',
    icon: (props) => <TrendingDownIcon {...props} />,
  },
  [CHART_TYPES.PIE]: {
    key: CHART_TYPES.PIE,
    label: 'Pie',
    icon: (props) => <PieChartIcon {...props} />,
  },
  [CHART_TYPES.NUMBER]: {
    key: CHART_TYPES.NUMBER,
    label: 'Number',
    icon: (props) => <TagIcon {...props} />,
  },
};

export const SUPPORTED_CHART_TYPE_DEFINITIONS = Object.values(
  CHART_TYPE_DEFINITIONS,
);

// Recharts color palette
export const DEFAULT_RECHARTS_COLORS = [
  '#79abff',
  '#f7bd51',
  '#85d7a2',
  '#ff878a',
  '#b8acff',
  '#77d3e9',
  '#fea06a',
  '#e39ef2',
  '#f6d861',
  '#c3e684',
  '#a1e7dd',
  '#ff95c9',
];

export const BURNDOWN_COLORS = {
  total: '#fea06a',
  totalStop: '#ffd7c0',
  remaining: '#b8acff',
  remainingStop_1: '#c4baff',
  remainingStop_2: '#dad4ff',
};

export const BURNUP_COLORS = {
  total: '#77d3e9',
  totalStop: '#98d4e3',
  complete: '#f7bd51',
  completeStop_1: '#f4ce89',
  completeStop_2: '#f5daa8',
};

export const CHART_COLORS = {
  WHITE: '#fff',
  LIGHT_LABELS: '#999',
  LABELS: '#5c5c5c',
  DARK_LABELS: '#1f2937',
  AXIS_STROKE: '#666',
  GRID_STROKE: '#e0e0e0',
};

// The rotated "Tasks (count in numbers)" axis title is drawn *inside* the
// plot (`position: 'insideBottomLeft'`) with no dedicated margin reserved
// for it, so on a short card it runs out of vertical room: the text gets
// clipped ("Tasks (count in nu…") and collides with the topmost Y tick.
//
// Whether it fits depends on the *label's own length*, not on a fixed card
// size — so this compares the rotated string's estimated vertical run
// against the plot height actually left after the X axis and chart margins.
const Y_AXIS_LABEL_FONT_SIZE = 12;
// A rotated string's vertical run ≈ chars × fontSize × this (average glyph
// advance for the mixed-case label text, deliberately on the generous side).
const Y_AXIS_LABEL_CHAR_RATIO = 0.5;
// Vertical space the plot loses before any of it is available to the
// rotated title: chart margins (~30px) + rotated X-axis tick labels (up to
// 80px) + , for the burn charts, their legend row (~34px).
const PLOT_VERTICAL_CHROME = 120;

export const MIN_WIDTH_FOR_Y_AXIS_LABEL = 320;

/**
 * True when the rotated Y-axis title has enough room to render without
 * being clipped or overlapping the top tick.
 *
 * @param {string} label — the axis title that would be rendered.
 * @param {{width: number, height: number}} size — measured container size.
 *        `{0,0}` (not yet measured) returns true, preserving the previous
 *        behaviour rather than flickering the label off on first paint.
 */
export const shouldShowYAxisLabel = (label, size) => {
  if (!label) return false;
  const { width, height } = size || {};
  if (!width || !height) return true;
  if (width < MIN_WIDTH_FOR_Y_AXIS_LABEL) return false;
  const requiredHeight =
    label.length * Y_AXIS_LABEL_FONT_SIZE * Y_AXIS_LABEL_CHAR_RATIO;
  return height - PLOT_VERTICAL_CHROME >= requiredHeight;
};

export const getYAxisLabelConfig = (value, style = {}) => ({
  value,
  angle: -90,
  position: 'insideBottomLeft',
  style: {
    textRendering: 'optimizeLegibility',
    shapeRendering: 'crispEdges',
    fontSize: 12,
    fill: CHART_COLORS.LABELS,
    fontWeight: '400',
    ...(style || {}),
  },
  offset: 25,
  dy: 0,
});

// ─── Chart-slice drill-down ────────────────────────────────────────────────
export const DRILLDOWN_KEY = '__drilldown';

// Mirrors the server's `isProvided` (ReportService.selectDrilldownTaskIds):
// values it does not consider provided are treated as "no constraint", so
// sending one would silently widen a slice click into "see all". `null` is a
// real, matchable aggregation value (e.g. an unassigned assignee/creator, or
// an untagged task rendered as "None") — only `undefined` (dimension not
// present on this row at all) and `''` mean "not provided".
const isDrilldownValue = (value) => value !== undefined && value !== '';

/**
 * Builds the `{ group?, stack?, date? }` descriptor for one clicked chart
 * element, to be resolved by `POST /report/:reportId/tasks/drilldown`.
 *
 * @param {Object} row — the prepared chart row that was clicked.
 * @param {string} [seriesKey] — the clicked series' dataKey for stacked and
 *        grouped bars (the DISPLAY stack label); absent for every other chart
 *        type, which means "the whole group".
 * @returns {Object|null} descriptor, or null when the row carries nothing that
 *          identifies it — in which case the caller must NOT navigate, since an
 *          empty descriptor means "see all" and would show the wrong tasks.
 */
export const getDrilldownDescriptor = (row, seriesKey) => {
  const raw = row?.[DRILLDOWN_KEY];
  if (!raw) return null;

  const stack = isDrilldownValue(seriesKey)
    ? raw.stacks?.[seriesKey]
    : raw.stack;

  const descriptor = {};
  if (isDrilldownValue(raw.group)) descriptor.group = raw.group;
  if (isDrilldownValue(stack)) descriptor.stack = stack;
  if (isDrilldownValue(raw.date)) descriptor.date = raw.date;

  return Object.keys(descriptor).length ? descriptor : null;
};

export const NON_FILTER_KEYS = new Set([
  'groupBy',
  'portfolioIds',
  'projectIds',
  'reportOn',
]);

export const FILTER_LABEL_MAP = {
  assignedTo: 'Assignee',
  creator: 'Creator',
  dueDate: 'Due Date',
  priority: 'Priority',
  status: 'Task completion status',
  tags: 'Tags',
};
